import React, { useEffect, useMemo, useState } from 'react';

type ApplicantActivity = {
  jobId: number;
  jobTitle: string;
  company: string;
  applicationStatus: string;
  dateApplied: string;
  jobStatus: string;
};

type ApplicantDashboardData = {
  applicationsSubmitted: number;
  applicationsAccepted: number;
  applicationsRejected: number;
  applicationsPending: number;
  jobsCompleted: number;
  totalHoursWorked: number;
  averageRating: number | null;
  totalReviews: number;
  completionRate: number;
  acceptanceRate: number;
  noShowRate: number;
  ratingsFromCompanies?: Array<{
    companyName: string;
    jobTitle: string;
    rating: number;
    comment: string | null;
    createdAt: string;
  }>;
  activity: ApplicantActivity[];
};

type Props = Readonly<{
  loading: boolean;
  data: ApplicantDashboardData | null;
}>;

export default function ApplicantDashboard({ loading, data }: Props) {
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const [activityPage, setActivityPage] = useState(1);
  const [metricTablePage, setMetricTablePage] = useState(1);
  const PAGE_SIZE = 10;

  const formatApplicationState = (status: string) => {
    if (status === 'RESPONSE_AWAITED') return 'RESPONSE_AWAITED';
    if (status === 'ACCEPTED') return 'ACCEPTED';
    if (status === 'REJECTED') return 'REJECTED';
    if (status === 'NO_SHOW') return 'NO_SHOW';
    return status;
  };

  const modalRows = useMemo(() => {
    if (!activeMetric || !data) return [] as Array<Record<string, React.ReactNode>>;

    if (activeMetric === 'Rating From Companies') {
      return (data.ratingsFromCompanies ?? []).map((review) => ({
        'Company Name': review.companyName,
        'Job Title': review.jobTitle,
        Rating: `⭐ ${review.rating.toFixed(1)}`,
        Comments: review.comment?.trim() ? review.comment : '—',
      }));
    }

    return data.activity.map((item) => ({
      'Job ID': item.jobId,
      'Job Title': item.jobTitle,
      Company: item.company,
      'Application Status': formatApplicationState(item.applicationStatus),
      'Date Applied': new Date(item.dateApplied).toLocaleString(),
      'Job Status': item.jobStatus,
    }));
  }, [activeMetric, data]);

  const modalColumns = modalRows.length > 0 ? Object.keys(modalRows[0]) : [];
  const totalActivityPages = Math.max(1, Math.ceil((data?.activity.length ?? 0) / PAGE_SIZE));
  const pagedActivity = (data?.activity ?? []).slice((activityPage - 1) * PAGE_SIZE, activityPage * PAGE_SIZE);
  const totalMetricPages = Math.max(1, Math.ceil(modalRows.length / PAGE_SIZE));
  const pagedModalRows = modalRows.slice((metricTablePage - 1) * PAGE_SIZE, metricTablePage * PAGE_SIZE);
  const closeMetricModal = () => {
    setActiveMetric(null);
    setMetricTablePage(1);
  };

  useEffect(() => {
    setMetricTablePage(1);
  }, [activeMetric]);

  useEffect(() => {
    setActivityPage((current) => Math.min(current, totalActivityPages));
  }, [totalActivityPages]);

  if (loading) {
    return <p className="empty-inline">Loading applicant dashboard...</p>;
  }

  if (!data) {
    return <p className="empty-inline">No dashboard data available.</p>;
  }

  const metrics: Array<[string, React.ReactNode]> = [
    ['Applications Submitted', data.applicationsSubmitted],
    ['Applications Accepted', data.applicationsAccepted],
    ['Applications Rejected', data.applicationsRejected],
    ['Applications Pending', data.applicationsPending],
    ['Jobs Completed', data.jobsCompleted],
    ['Total Hours Worked', `${data.totalHoursWorked.toFixed(2)} hrs`],
    ['Rating From Companies', data.averageRating ? `⭐ ${data.averageRating.toFixed(1)} (${data.totalReviews})` : '⭐ New'],
    ['Completion Rate', `${data.completionRate.toFixed(2)}%`],
    ['Acceptance Rate', `${data.acceptanceRate.toFixed(2)}%`],
    ['No Show Rate', `${data.noShowRate.toFixed(2)}%`],
  ];

  return (
    <div className="company-jobs-list">
      <div className="applicants-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
        {metrics.map(([label, value]) => (
          <button
            key={label}
            type="button"
            className="applicant-card"
            onClick={() => setActiveMetric(label)}
            style={{ textAlign: 'left', cursor: 'pointer' }}
          >
            <div className="applicant-info">
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          </button>
        ))}
      </div>

      <div className="company-job-card">
        <div className="company-job-header" style={{ cursor: 'default' }}>
          <div className="company-job-info">
            <h3>Recent Activity</h3>
          </div>
        </div>
        <div className="applicants-section" style={{ overflowX: 'auto' }}>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Job Title</th>
                <th>Company</th>
                <th>Application Status</th>
                <th>Date Applied</th>
              </tr>
            </thead>
            <tbody>
              {pagedActivity.map((item) => (
                <tr key={`${item.jobId}-${item.dateApplied}`}>
                  <td>{item.jobId}</td>
                  <td>{item.jobTitle}</td>
                  <td>{item.company}</td>
                  <td>{formatApplicationState(item.applicationStatus)}</td>
                  <td>{new Date(item.dateApplied).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.activity.length > PAGE_SIZE ? (
            <div className="applicants-pagination">
              <button
                className="btn-ghost"
                disabled={activityPage <= 1}
                onClick={() => setActivityPage((page) => Math.max(1, page - 1))}
              >
                Previous
              </button>
              <span>Page {activityPage} / {totalActivityPages}</span>
              <button
                className="btn-ghost"
                disabled={activityPage >= totalActivityPages}
                onClick={() => setActivityPage((page) => Math.min(totalActivityPages, page + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {activeMetric && (
        <div className="modal-overlay">
          <button
            type="button"
            className="modal-overlay-hitbox"
            onClick={closeMetricModal}
            aria-label="Close metric details"
          />
          <dialog
            className="modal modal-wide modal-dialog-open"
            open
            onCancel={(event) => {
              event.preventDefault();
              closeMetricModal();
            }}
          >
            <button className="modal-close" onClick={closeMetricModal}>×</button>
            <h2 className="modal-form-title">{activeMetric} Details</h2>
            {activeMetric === 'Rating From Companies' ? (
              <p className="empty-inline" style={{ marginBottom: 12 }}>
                Overall Average: {data.averageRating ? `⭐ ${data.averageRating.toFixed(1)}` : '⭐ New'} ({data.totalReviews} review{data.totalReviews === 1 ? '' : 's'})
              </p>
            ) : null}
            <div style={{ overflowX: 'auto' }}>
              <table className="dashboard-table">
                <thead>
                  <tr>
                    {modalColumns.map((column) => (
                      <th key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedModalRows.map((row) => {
                    const rowKey = JSON.stringify(row);
                    return (
                      <tr key={`app-metric-row-${rowKey}`}>
                        {modalColumns.map((column) => (
                          <td key={`${rowKey}-${column}`}>{row[column]}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {modalRows.length > PAGE_SIZE ? (
              <div className="applicants-pagination">
                <button
                  className="btn-ghost"
                  disabled={metricTablePage <= 1}
                  onClick={() => setMetricTablePage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <span>Page {metricTablePage} / {totalMetricPages}</span>
                <button
                  className="btn-ghost"
                  disabled={metricTablePage >= totalMetricPages}
                  onClick={() => setMetricTablePage((page) => Math.min(totalMetricPages, page + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </dialog>
        </div>
      )}
    </div>
  );
}
